import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createSlug, getProductUrl } from "../../../lib/slug";
import { categories } from "../../../mocks/categories";
import { supabase, SanPhamHanQuoc } from "../../../lib/supabase";

interface ProductCardProps {
  product: SanPhamHanQuoc;
  isKr: boolean;
}

const getProductThumb = (product: SanPhamHanQuoc): string => {
  const rawMedia = (product as unknown as Record<string, unknown>).media_items;
  const mediaItems: Array<{ url: string; type: string; is_cover?: boolean }> = Array.isArray(rawMedia)
    ? rawMedia as Array<{ url: string; type: string; is_cover?: boolean }>
    : [];
  const cover = mediaItems.find((m) => m.is_cover && m.type === "image");
  if (cover) return cover.url;
  const firstImg = mediaItems.find((m) => m.type === "image");
  if (firstImg) return firstImg.url;
  const images = Array.isArray(product.images) ? product.images as string[] : [];
  return images[0] ?? "";
};

const ProductCard = ({ product, isKr }: ProductCardProps) => {
  const thumb = getProductThumb(product);
  return (
    <Link
      to={getProductUrl({ companyName: product.company_name ?? "", name: product.name })}
      className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-[#004A99]/30 transition-all duration-300 group cursor-pointer flex flex-col"
    >
      <div className="relative w-full aspect-[4/3] bg-gray-50 overflow-hidden">
        {thumb ? (
          <img
            src={thumb}
            alt={isKr ? (product.name_kr ?? product.name) : product.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#004A99]/5">
            <i className="ri-box-3-line text-[#004A99]/30 text-3xl" />
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col flex-1">
        <h3 className="text-xs font-semibold text-[#212529] line-clamp-2 mb-1 leading-snug group-hover:text-[#004A99] transition-colors">
          {isKr && product.name_kr ? product.name_kr : product.name}
        </h3>
        <p className="text-[11px] text-gray-400 truncate mt-auto">
          {product.company_name || (product.sub_category_name ?? product.category_name)}
        </p>
      </div>
    </Link>
  );
};

interface OtherProductsGridProps {
  allProducts: SanPhamHanQuoc[];
  featuredProducts: SanPhamHanQuoc[];
  isKr: boolean;
  loading: boolean;
  skeletonGrid: (count: number) => JSX.Element;
}

const OtherProductsGrid = ({ allProducts, featuredProducts, isKr, loading, skeletonGrid }: OtherProductsGridProps) => {
  if (loading) return skeletonGrid(8);
  const featuredIds = new Set(featuredProducts.map((p) => p.id));
  const otherProducts = allProducts.filter((p) => !featuredIds.has(p.id));
  if (otherProducts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-100">
        <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-full mb-3">
          <i className="ri-inbox-line text-2xl text-gray-300" />
        </div>
        <p className="text-gray-400 text-sm">
          {isKr ? "해당 카테고리에 상품이 없습니다" : "Chưa có sản phẩm trong danh mục này"}
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
      {otherProducts.map((p) => (
        <ProductCard key={p.id} product={p} isKr={isKr} />
      ))}
    </div>
  );
};

const CategoryProductBrowser = () => {
  const { i18n } = useTranslation();
  const isKr = i18n.language === "kr";
  const [selectedCatId, setSelectedCatId] = useState<string>("all");
  const [featuredProducts, setFeaturedProducts] = useState<SanPhamHanQuoc[]>([]);
  const [allProducts, setAllProducts] = useState<SanPhamHanQuoc[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Fetch ALL products sorted by created_at desc
        let allQuery = supabase
          .from("san_pham_han_quoc")
          .select("id, name, name_kr, images, media_items, company_name, category_id, category_name, sub_category_name, is_featured, created_at")
          .order("created_at", { ascending: false });

        if (selectedCatId !== "all") {
          allQuery = allQuery.eq("category_id", selectedCatId);
        }

        const allRes = await allQuery;
        if (allRes.data) {
          const all = allRes.data as SanPhamHanQuoc[];
          // Newest 4 = "Sản phẩm mới nhất", rest = "Sản phẩm khác"
          setFeaturedProducts(all.slice(0, 4));
          setAllProducts(all);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCatId]);

  const catName = (cat: { name: string; nameKr: string }) =>
    isKr ? cat.nameKr : cat.name;

  const skeletonGrid = (count: number) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="bg-white rounded-xl overflow-hidden border border-gray-100 animate-pulse">
          <div className="aspect-[4/3] bg-gray-100" />
          <div className="p-3 space-y-2">
            <div className="h-3 w-full bg-gray-100 rounded" />
            <div className="h-3 w-2/3 bg-gray-100 rounded" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section className="py-10 md:py-12 bg-[#F8F9FA]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        {/* Section Title */}
        <div className="flex items-end justify-between mb-4 md:mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1 h-5 bg-[#004A99] rounded-full" />
              <span className="text-[10px] font-bold text-[#004A99] uppercase tracking-widest">
                {isKr ? "한국 상품 쇼케이스" : "SHOWCASE SẢN PHẨM HÀN QUỐC"}
              </span>
            </div>
            <h2 className="text-xl md:text-2xl font-black text-[#212529]">
              {isKr ? "한국 신상품 & 추천 상품" : "Sản Phẩm Hàn Quốc"}
            </h2>
          </div>
          <Link
            to="/danh-muc"
            className="flex items-center gap-1.5 text-sm text-[#004A99] font-semibold hover:text-[#003d7d] cursor-pointer whitespace-nowrap"
          >
            {isKr ? "전체 보기" : "Xem tất cả"}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Category Filter — Grid on mobile, horizontal scroll on desktop */}
        {/* Mobile: 3-column grid */}
        <div className="mb-6 md:hidden">
          <div className="grid grid-cols-3 gap-1.5">
            <button
              onClick={() => setSelectedCatId("all")}
              className={`px-2 py-2 rounded-lg text-[12px] font-semibold transition-all cursor-pointer text-center leading-tight ${
                selectedCatId === "all"
                  ? "bg-[#004A99] text-white"
                  : "bg-white border border-gray-200 text-[#212529]"
              }`}
            >
              {isKr ? "전체" : "Tất Cả"}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCatId(cat.id)}
                className={`px-2 py-2 rounded-lg text-[12px] font-semibold transition-all cursor-pointer text-center leading-tight ${
                  selectedCatId === cat.id
                    ? "bg-[#004A99] text-white"
                    : "bg-white border border-gray-200 text-[#212529]"
                }`}
              >
                {catName(cat)}
              </button>
            ))}
          </div>
        </div>
        {/* Desktop: horizontal scroll */}
        <div
          ref={scrollRef}
          className="hidden md:flex items-center gap-2 mb-6 overflow-x-auto pb-0 flex-wrap"
        >
          <button
            onClick={() => setSelectedCatId("all")}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              selectedCatId === "all"
                ? "bg-[#004A99] text-white"
                : "bg-white border border-gray-200 text-[#212529] hover:border-[#004A99] hover:text-[#004A99]"
            }`}
          >
            {isKr ? "전체" : "Tất Cả"}
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCatId(cat.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                selectedCatId === cat.id
                  ? "bg-[#004A99] text-white"
                  : "bg-white border border-gray-200 text-[#212529] hover:border-[#004A99] hover:text-[#004A99]"
              }`}
            >
              {catName(cat)}
            </button>
          ))}
        </div>

        {/* ── PHẦN 1: SẢN PHẨM MỚI NHẤT ── */}
        {(loading || featuredProducts.length > 0) && (
          <div className="mb-8 md:mb-10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-lg">
                  <i className="ri-star-fill text-amber-500 text-xs" />
                </div>
                <h3 className="text-xl md:text-2xl font-black text-[#212529]">
                  {isKr ? "최신 상품" : "Sản Phẩm Mới Nhất"}
                </h3>
                {!loading && (
                  <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {featuredProducts.length} {isKr ? "개" : "sản phẩm"}
                  </span>
                )}
              </div>
            </div>

            {loading ? skeletonGrid(4) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {featuredProducts.map((p) => (
                  <ProductCard key={p.id} product={p} isKr={isKr} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── PHẦN 2: SẢN PHẨM KHÁC ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center bg-[#004A99]/10 rounded-lg">
                <i className="ri-apps-line text-[#004A99] text-xs" />
              </div>
              <h3 className="text-xl md:text-2xl font-black text-[#212529]">
                {isKr ? "다른 상품" : "Sản Phẩm Khác"}
              </h3>
            </div>
          </div>

          <OtherProductsGrid
            allProducts={allProducts}
            featuredProducts={featuredProducts}
            isKr={isKr}
            loading={loading}
            skeletonGrid={skeletonGrid}
          />

          {/* View all CTA */}
          <div className="mt-6 md:mt-8 text-center">
            <Link
              to="/danh-muc"
              className="inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-3.5 border-2 border-[#004A99] text-[#004A99] hover:bg-[#004A99] hover:text-white rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-grid-line" />
              {isKr ? "전체 상품 보기" : "Xem Tất Cả Sản Phẩm"}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoryProductBrowser;
